/**
 * Autosave snapshots to IndexedDB. A debounced Autosaver writes an "auto"
 * snapshot 2s after the last change; explicit saves write a "save" checkpoint.
 * On launch, recoveryCandidate() reports an unsaved auto snapshot newer than
 * the last explicit save.
 */
import { getDB, type Snapshot } from "./db";

export const MAX_SNAPSHOTS_PER_UUID = 20;
export const AUTOSAVE_DEBOUNCE_MS = 2000;

/** Write a snapshot and prune to the newest MAX per uuid. */
export async function writeSnapshot(snap: Omit<Snapshot, "seq">): Promise<void> {
  const db = await getDB();
  await db.add("snapshots", snap as Snapshot);

  const tx = db.transaction("snapshots", "readwrite");
  const index = tx.store.index("by-uuid");
  const keys = await index.getAllKeys(snap.uuid);
  if (keys.length > MAX_SNAPSHOTS_PER_UUID) {
    // keys come back in ascending seq order → oldest first.
    const toDelete = keys.slice(0, keys.length - MAX_SNAPSHOTS_PER_UUID);
    for (const key of toDelete) await tx.store.delete(key);
  }
  await tx.done;
}

/** Newest snapshot (any kind) for a document. */
export async function latestSnapshot(uuid: string): Promise<Snapshot | undefined> {
  const db = await getDB();
  const all = await db.getAllFromIndex("snapshots", "by-uuid", uuid);
  if (all.length === 0) return undefined;
  return all.reduce((a, b) => (b.ts >= a.ts ? b : a));
}

/**
 * An unsaved auto snapshot to offer for recovery: the newest "auto" snapshot,
 * but only if it is newer than the newest "save" checkpoint.
 */
export async function recoveryCandidate(uuid: string): Promise<Snapshot | undefined> {
  const db = await getDB();
  const all = await db.getAllFromIndex("snapshots", "by-uuid", uuid);
  let lastAuto: Snapshot | undefined;
  let lastSave: Snapshot | undefined;
  for (const s of all) {
    if (s.kind === "auto" && (!lastAuto || s.ts > lastAuto.ts)) lastAuto = s;
    if (s.kind === "save" && (!lastSave || s.ts > lastSave.ts)) lastSave = s;
  }
  if (lastAuto && (!lastSave || lastAuto.ts > lastSave.ts)) return lastAuto;
  return undefined;
}

/** Remove all snapshots for a document (e.g. after the user discards recovery). */
export async function clearSnapshots(uuid: string): Promise<void> {
  const db = await getDB();
  const tx = db.transaction("snapshots", "readwrite");
  const keys = await tx.store.index("by-uuid").getAllKeys(uuid);
  for (const key of keys) await tx.store.delete(key);
  await tx.done;
}

/**
 * Debounced autosaver. `produce` returns the current serialized document; the
 * saver stamps it and writes an auto snapshot after the debounce window.
 */
export interface SnapshotInput {
  uuid: string;
  xml: string;
  filename: string;
}

export class Autosaver {
  private timer: ReturnType<typeof setTimeout> | null = null;
  constructor(
    private readonly produce: () => SnapshotInput,
    private readonly now: () => number = () => Date.now(),
    private readonly delayMs: number = AUTOSAVE_DEBOUNCE_MS,
  ) {}

  schedule(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), this.delayMs);
  }

  async flush(): Promise<void> {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
    const input = this.produce();
    // Never persist an empty snapshot (e.g. produced before the view mounts);
    // it would be an unrestorable recovery candidate.
    if (!input.xml) return;
    await writeSnapshot({ ...input, ts: this.now(), kind: "auto" });
  }

  cancel(): void {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}
